import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { checkPaymentByToken, checkPaymentStatus } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, XCircle, Clock, Loader2, ArrowRight, RefreshCw } from 'lucide-react';

export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [retries, setRetries] = useState(0);
  const pollingRef = useRef(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const pending = localStorage.getItem('pending_payment');
    let pendingData = null;
    try { pendingData = JSON.parse(pending); } catch {}

    const checkStatus = async () => {
      try {
        let result = null;
        if (token) {
          result = await checkPaymentByToken(token);
        } else if (pendingData?.commerce_order) {
          result = await checkPaymentStatus(pendingData.commerce_order);
        }
        if (!result) {
          setStatus('not_found');
          return;
        }
        setPaymentInfo(result);
        if (result.status === 'completed' || result.is_enrolled) {
          setStatus('success');
          localStorage.removeItem('pending_payment');
          if (pollingRef.current) clearInterval(pollingRef.current);
        } else if (result.status === 'rejected') {
          setStatus('rejected');
          localStorage.removeItem('pending_payment');
          if (pollingRef.current) clearInterval(pollingRef.current);
        } else if (result.status === 'cancelled') {
          setStatus('cancelled');
          localStorage.removeItem('pending_payment');
          if (pollingRef.current) clearInterval(pollingRef.current);
        } else {
          setStatus('pending');
        }
      } catch (err) {
        console.error('Payment check failed:', err);
        if (retries > 8) {
          setStatus('error');
          if (pollingRef.current) clearInterval(pollingRef.current);
        }
      }
    };

    checkStatus();
    pollingRef.current = setInterval(() => {
      setRetries(r => r + 1);
      checkStatus();
    }, 3000);

    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [searchParams]);

  useEffect(() => {
    if (retries > 10 && status === 'pending') {
      setStatus('timeout');
      if (pollingRef.current) clearInterval(pollingRef.current);
    }
  }, [retries, status]);

  const statusConfig = {
    loading: { icon: <Loader2 className="h-16 w-16 text-[#8B1538] animate-spin" />, title: 'Verificando pago...', desc: 'Estamos confirmando tu pago con Flow.cl', color: 'text-[#8B1538]' },
    success: { icon: <CheckCircle2 className="h-16 w-16 text-[#2D6A4F]" />, title: 'Pago exitoso!', desc: 'Tu inscripcion ha sido activada correctamente.', color: 'text-[#2D6A4F]' },
    pending: { icon: <Clock className="h-16 w-16 text-[#B8860B] animate-pulse" />, title: 'Pago en proceso...', desc: 'Tu pago esta siendo procesado. Se activara tu inscripcion automaticamente.', color: 'text-[#B8860B]' },
    rejected: { icon: <XCircle className="h-16 w-16 text-[#9B2226]" />, title: 'Pago rechazado', desc: 'El pago fue rechazado. Puedes intentar nuevamente.', color: 'text-[#9B2226]' },
    cancelled: { icon: <XCircle className="h-16 w-16 text-[#737373]" />, title: 'Pago cancelado', desc: 'El pago fue cancelado. Puedes intentar nuevamente.', color: 'text-[#737373]' },
    error: { icon: <XCircle className="h-16 w-16 text-[#9B2226]" />, title: 'Error al verificar', desc: 'No pudimos verificar el estado del pago. Contacta soporte.', color: 'text-[#9B2226]' },
    not_found: { icon: <XCircle className="h-16 w-16 text-[#737373]" />, title: 'Pago no encontrado', desc: 'No se encontro informacion del pago.', color: 'text-[#737373]' },
    timeout: { icon: <Clock className="h-16 w-16 text-[#B8860B]" />, title: 'Verificacion en progreso', desc: 'La confirmacion esta tardando. Tu inscripcion se activara cuando Flow confirme el pago.', color: 'text-[#B8860B]' },
  };

  const config = statusConfig[status] || statusConfig.loading;

  return (
    <Layout>
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]" data-testid="payment-return-page">
        <Card className="bg-white border border-[#E5DFD3] shadow-sm max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mb-6">{config.icon}</div>
            <h2 className={`font-heading text-2xl mb-2 ${config.color}`} data-testid="payment-status-title">{config.title}</h2>
            <p className="text-[#737373] mb-6">{config.desc}</p>

            {paymentInfo && (
              <div className="bg-[#F5F2EA] rounded-lg p-4 mb-6 text-left space-y-2">
                {paymentInfo.module_title && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#737373]">Modulo:</span>
                    <span className="font-medium text-[#1A1A1A]">{paymentInfo.module_title}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-[#737373]">Monto:</span>
                  <span className="font-medium text-[#1A1A1A]">${paymentInfo.amount?.toLocaleString()} CLP</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#737373]">Plan:</span>
                  <span className="font-medium text-[#1A1A1A]">{paymentInfo.subscription_type === 'annual' ? 'Pago Anticipado' : 'Mensualidad'}</span>
                </div>
                {paymentInfo.commerce_order && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#737373]">Orden:</span>
                    <span className="font-mono text-xs text-[#4A4A4A]">{paymentInfo.commerce_order}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              {status === 'success' && paymentInfo?.course_id && (
                <Button data-testid="go-to-course-btn" onClick={() => navigate(`/student/course/${paymentInfo.course_id}`)} className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white">
                  <ArrowRight className="h-4 w-4 mr-2" /> Ir al Modulo
                </Button>
              )}
              {(status === 'rejected' || status === 'cancelled') && paymentInfo?.course_id && (
                <Button data-testid="retry-payment-btn" onClick={() => navigate(`/student/course/${paymentInfo.course_id}`)} className="w-full bg-[#8B1538] hover:bg-[#70102B] text-white">
                  <RefreshCw className="h-4 w-4 mr-2" /> Intentar de nuevo
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/student')} className="w-full border-[#E5DFD3]">
                Volver al Panel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
